import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { listPublishedPosts, type PublicPost } from "@/lib/blog.functions";

export const Route = createFileRoute("/blog/")({
  loader: () => listPublishedPosts(),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Smart Home & AV Blog — Guides & How-To | Apex AV" },
      {
        name: "description",
        content:
          "Practical guides on home theater, smart home platforms, lighting, networking, and outdoor AV from Denver's working AV integrators.",
      },
      { property: "og:title", content: "Smart Home & AV Blog | Apex AV" },
      {
        property: "og:description",
        content:
          "Guides on home theater, smart home platforms, lighting, and networking from Denver's trusted AV integrators.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://apexav.com/blog" },
      { name: "twitter:title", content: "Smart Home & AV Blog | Apex AV" },
      { name: "twitter:description", content: "Guides on home theater, smart home platforms, lighting, and networking from Denver's trusted AV integrators." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Apex AV Blog",
          url: "https://apexav.com/blog",
          publisher: { "@id": "https://apexav.com/#organization" },
          blogPost: (loaderData ?? []).map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `https://apexav.com/blog/${p.slug}`,
            datePublished: p.published_at ?? p.updated_at,
            dateModified: p.updated_at,
            description: p.description,
          })),
        }),
      },
    ],
  }),
  errorComponent: ({ error }) => (
    <section className="container mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-3xl font-bold">Couldn't load articles</h1>
      <p className="mt-3 text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </section>
  ),
  component: BlogIndex,
});

function BlogIndex() {
  const posts = Route.useLoaderData() as PublicPost[];
  return (
    <>
      <section className="container mx-auto px-6 pt-20 pb-12">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-brand-text uppercase tracking-wider">
            Resources
          </p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold">
            Smart home &amp; AV guides.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            Real-world planning advice and checklists from the team installing them every week
            across the Front Range.
          </p>
        </div>
      </section>


      <section className="container mx-auto px-6 pb-12">
        <div className="max-w-3xl">
          <h2 className="font-display text-2xl md:text-3xl font-bold">What these guides cover</h2>
          <p className="mt-4 text-muted-foreground">
            These articles come out of questions we actually get on-site and during consultations — how to size a home theater, which smart home platform fits a given budget, how much lighting control adds to a remodel, and what a solid home network needs to look like before you add cameras and streaming devices on top of it. We write from what we've installed and serviced across the Denver metro, not from generic product marketing.
          </p>
          <h2 className="mt-8 font-display text-2xl md:text-3xl font-bold">Who they're for</h2>
          <p className="mt-4 text-muted-foreground">
            If you're planning a new build or remodel and trying to figure out what to prewire, comparing smart home ecosystems before committing to one, or just trying to understand what's realistic to retrofit into a finished house, these guides are meant to give you enough context to have a more useful conversation with any installer — including us. They're not sales pages; they're the same planning information we'd walk through in person during a free consultation.
          </p>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-12">
        <Link
          to="/blog/custom-home-theater-design-guide"
          className="group block rounded-xl border border-brand-text/40 bg-card/60 p-8 hover:bg-card/80 transition-colors"
        >
          <p className="text-xs font-semibold text-brand-text uppercase tracking-wider">
            Featured guide
          </p>
          <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold group-hover:text-brand-text transition-colors">
            Custom Home Theater Design Guide
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Room sizing, seating distance, acoustic treatment, projection vs. flat panel, and
            budgets — everything we plan before building a Denver theater.
          </p>
          <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-text">
            Read the guide <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </Link>
      </section>

      <section className="container mx-auto px-6 pb-24">

        {posts.length === 0 ? (
          <p className="text-muted-foreground">No articles published yet.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Link
                key={post.slug}
                to="/blog/$slug"
                params={{ slug: post.slug }}
                className="group rounded-xl border border-border bg-card/40 p-6 hover:border-brand-text/50 hover:bg-card/70 transition-colors"
              >
                <p className="text-xs font-semibold text-brand-text uppercase tracking-wider">
                  {post.category}
                </p>
                <h2 className="mt-3 font-display text-xl font-bold leading-tight group-hover:text-brand-text transition-colors">
                  {post.title}
                </h2>
                <p className="mt-3 text-sm text-muted-foreground line-clamp-3">
                  {post.description}
                </p>
                <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
                  {post.published_at && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(post.published_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        timeZone: "UTC",
                      })}

                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {post.read_minutes} min read
                  </span>
                </div>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-text">
                  Read article <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
